import type { ConsumerApiClient } from '../client';

// ── Types ──────────────────────────────────────────────────────

export type DraftProductType = 'letter' | 'photo' | 'greeting_card';

/** Contact relation returned nested by the API */
interface DraftContactRelation {
  id: string;
  recipientId?: string;
  facilityId?: string;
  recipient?: { firstName?: string; lastName?: string; inmateNumber?: string };
  facility?: { name?: string };
}

/** Raw shape the API returns */
interface DraftApiResponse {
  id: string;
  userId: string;
  contactId: string | null;
  productType: DraftProductType;
  data: Record<string, any>;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  contact?: DraftContactRelation | null;
}

/** Flat draft shape used by screens */
export interface Draft {
  id: string;
  contactId: string | null;
  contactName: string;
  facilityName: string;
  productType: DraftProductType;
  data: Record<string, any>;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

/** Normalise nested contact relation into a flat contactName */
function normaliseDraft(raw: DraftApiResponse): Draft {
  let contactName = '';
  if (raw.contact) {
    const r = raw.contact.recipient;
    if (r) {
      contactName = [r.firstName, r.lastName].filter(Boolean).join(' ');
    }
  }
  // Fall back to data.contactName if stored there
  if (!contactName && raw.data?.contactName) {
    contactName = raw.data.contactName;
  }

  // Resolve facility name
  let facilityName = '';
  if (raw.contact?.facility?.name) {
    facilityName = raw.contact.facility.name;
  }
  if (!facilityName && raw.data?.facilityName) {
    facilityName = raw.data.facilityName;
  }

  return {
    id: raw.id,
    contactId: raw.contactId,
    contactName,
    facilityName,
    productType: raw.productType,
    data: raw.data,
    expiresAt: raw.expiresAt,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

// ── Request types ──────────────────────────────────────────────

export interface CreateDraftRequest {
  contactId?: string;
  productType: DraftProductType;
  data?: Record<string, any>;
}

export interface UpdateDraftRequest {
  contactId?: string;
  productType?: DraftProductType;
  data?: Record<string, any>;
}

// ── API ────────────────────────────────────────────────────────

export interface DraftsApi {
  /** Fetch all drafts for the current user */
  listDrafts(): Promise<Draft[]>;
  /** Fetch a single draft */
  getDraft(id: string): Promise<Draft>;
  /** Create a new draft (server sets 60-day expiry) */
  createDraft(req: CreateDraftRequest): Promise<Draft>;
  /** Update an existing draft */
  updateDraft(id: string, req: UpdateDraftRequest): Promise<Draft>;
  /** Delete a single draft */
  deleteDraft(id: string): Promise<void>;
  /** Delete all expired drafts (call on app launch) */
  deleteExpiredDrafts(): Promise<{ deleted: number }>;
}

export function createDraftsApi(client: ConsumerApiClient): DraftsApi {
  return {
    async listDrafts() {
      const raw = await client.get<DraftApiResponse[]>('/drafts');
      return raw.map(normaliseDraft);
    },

    async getDraft(id) {
      const raw = await client.get<DraftApiResponse>(`/drafts/${id}`);
      return normaliseDraft(raw);
    },

    async createDraft(req) {
      const raw = await client.post<DraftApiResponse>('/drafts', req);
      return normaliseDraft(raw);
    },

    async updateDraft(id, req) {
      const raw = await client.put<DraftApiResponse>(`/drafts/${id}`, req);
      return normaliseDraft(raw);
    },

    deleteDraft(id) {
      return client.delete(`/drafts/${id}`);
    },

    deleteExpiredDrafts() {
      return client.delete('/drafts/expired');
    },
  };
}
